import React, { useState, useEffect } from 'react';
import { useWebSocket } from './WebSocketContext';
import { useSnackbar } from '../Utils/SnackBarContext';
import {
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { useTranslation } from 'react-i18next';
import SettingIntelligentTag from './SettingIntelligentTag';
import SettingDataExport from './SettingDataExport';
import SettingOthers from './SettingOthers';
import SettingAI from './SettingAI';
import SettingFilePasswords from './SettingFilePasswords';
import CaseDeleteDialog from './CaseDeleteDialog';
import { useCaseDelete } from '../hooks/useCaseDelete';

// TabPanel helper
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{
        display: value !== index ? 'none' : 'block',
        minHeight: 'auto' // Removed fixed height for better responsiveness
      }}
      {...other}
    >
      <Box sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        minHeight: { xs: 'auto', md: '350px' }, // Only keep min height on larger screens
        display: 'flex',
        flexDirection: 'column'
      }}>
        {children}
      </Box>
    </div>
  );
}

const Settings = ({ caseid, tags }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { ws, sendMessage, isConnected, subscribeToMessages } = useWebSocket();
  const { openSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [rescanConfirmOpen, setRescanConfirmOpen] = useState(false);
  const [redoAnalysisConfirmOpen, setRedoAnalysisConfirmOpen] = useState(false);

  // Use case delete hook for DRY approach
  const {
    deleteDialogOpen,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
    isDeleting
  } = useCaseDelete({
    sendMessage,
    isConnected,
    navigateOnDelete: true,
    currentCaseId: caseid
  });
  // isDeleting flag is now returned from the hook

  // Listen for forceScanFolder responses
  useEffect(() => {
    const unsubscribe = subscribeToMessages(['forceScanFolder'], (message) => {
      console.log('Received forceScanFolder message:', message);
      const data = message.data || {};
      console.log('Extracted data:', data);

      if (data.status === 'error') {
        console.log('Showing error snackbar:', data.message);
        openSnackbar(data.message || t('settings.rescan_error') || 'Failed to rescan folder', 5000);
      } else if (data.status === 'success') {
        console.log('Showing success snackbar');
        openSnackbar(t('settings.rescan_success') || 'Folder scan initiated successfully', 3000);
      } else if (data.error) {
        console.log('Showing error snackbar (legacy format):', data.error);
        openSnackbar(data.error, 5000);
      } else {
        console.log('Unknown forceScanFolder response format:', data);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, openSnackbar, t]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAction = (command, message, callback) => {
    if (ws) {
      ws.send(JSON.stringify({ command, caseid }));
      // alert(t(message));
      if (callback) callback();
    } else {
      console.error('WebSocket connection is not available.');
    }
  };

  const handleDeleteClick = () => {
    // Open delete dialog using the hook
    openDeleteDialog({ id: caseid, title: t('settings.current_case') || 'this case' });
  };

  const handleRescanClick = () => {
    setRescanConfirmOpen(true);
  };

  const handleRescanConfirm = () => {
    setRescanConfirmOpen(false);
    handleAction('forceScanFolder', 'settings.analysis_deleted_ready_rescan');
  };

  const handleRescanCancel = () => {
    setRescanConfirmOpen(false);
  };

  const handleRedoAnalysisClick = () => {
    setRedoAnalysisConfirmOpen(true);
  };

  const handleRedoAnalysisConfirm = () => {
    setRedoAnalysisConfirmOpen(false);
    handleAction('rebuilddata', 'settings.analysis_deleted_ready_rescan', () =>
      window.location.reload()
    );
  };

  const handleRedoAnalysisCancel = () => {
    setRedoAnalysisConfirmOpen(false);
  };

  return (
    <>
      <Card sx={{ borderRadius: { xs: 2.5, sm: 5 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ textAlign: 'center', paddingTop: "10px !important" }}>
            <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('settings.title')}
            </Typography>

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isTablet ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Settings Tabs"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#5757b0', // Match button color
                },
                '& .MuiTab-root': {
                  color: 'text.secondary',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: 100, sm: 'auto' },
                  '&.Mui-selected': {
                    color: '#5757b0', // Match button color
                    fontWeight: 'bold'
                  },
                  '&:hover': {
                    color: '#aaaaca', // Match button hover color
                    opacity: 1,
                  },
                  transition: 'color 0.3s ease',
                },
                '& .MuiTabs-root': {
                  minHeight: 48, // Consistent tab height
                },
              }}
            >
              <Tab label={t('settings.intelligent_tags')} id="tab-0" aria-controls="tabpanel-0" />
              {/* Data Connectors moved to header - removed from settings */}
              <Tab label={t('settings.data_export_automation')} id="tab-1" aria-controls="tabpanel-1" />
              <Tab label={t('settings.ai.title', 'AI')} id="tab-2" aria-controls="tabpanel-2" />
              <Tab label={t('settings.file_password.title', 'File Password')} id="tab-3" aria-controls="tabpanel-3" />
              <Tab label={t('settings.others.title')} id="tab-4" aria-controls="tabpanel-4" />
            </Tabs>
            <Divider sx={{ my: 2 }} />

            {/* Tab Content Container */}
            <Box sx={{
              position: 'relative',
              minHeight: { xs: 'auto', md: '400px' },
              mt: 2
            }}>
              {/* Intelligent Tags Tab */}
              <TabPanel value={tabValue} index={0}>
                <SettingIntelligentTag />
              </TabPanel>

              {/* Data Export Tab */}
              <TabPanel value={tabValue} index={1}>
                <SettingDataExport caseid={caseid} />
              </TabPanel>

              {/* AI Tab */}
              <TabPanel value={tabValue} index={2}>
                <SettingAI caseid={caseid} />
              </TabPanel>

              {/* File Passwords Tab */}
              <TabPanel value={tabValue} index={3}>
                <SettingFilePasswords caseid={caseid} />
              </TabPanel>

              {/* Others Tab */}
              <TabPanel value={tabValue} index={4}>
                <SettingOthers caseid={caseid} />
              </TabPanel>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* New Analysis Actions Card */}
      <Card sx={{ borderRadius: { xs: 2.5, sm: 5 }, mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 3, mt: { xs: 1, sm: 3 } }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FindInPageIcon />}
              onClick={handleRescanClick}
              title={t('settings.redo_analysis_tooltip')}
              sx={{
                backgroundColor: '#5757b0',
                color: '#fff',
                '&:hover': { backgroundColor: '#aaaaca' },
                py: 1.5,
                fontSize: '12px',
                borderRadius: 3,
                boxShadow: 4,
                width: { xs: '100%', sm: '300px' },
                mx: 'auto',
              }}
            >
              {t('settings.rescan')}
            </Button>
            <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
              {t('settings.rescan_note')}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 3, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={handleRedoAnalysisClick}
              title={t('settings.redo_analysis_tooltip')}
              sx={{
                backgroundColor: '#5757b0',
                color: '#fff',
                '&:hover': { backgroundColor: '#aaaaca' },
                py: 1.5,
                fontSize: '12px',
                borderRadius: 3,
                boxShadow: 4,
                width: { xs: '100%', sm: '300px' },
                mx: 'auto',
              }}
            >
              {t('settings.redo_analysis')}
            </Button>
            <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
              {t('settings.redo_analysis_note')}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={isDeleting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <DeleteIcon />}
              disabled={isDeleting}
              onClick={handleDeleteClick}
              title={t('settings.delete_analysis_tooltip')}
              sx={{
                backgroundColor: '#ff6666',
                '&:hover': { backgroundColor: '#ff9999' },
                color: '#fff',
                py: 1.5,
                fontSize: '12px',
                borderRadius: 3,
                boxShadow: 4,
                width: { xs: '100%', sm: '300px' },
                mx: 'auto',
              }}
            >
              {t('settings.delete_analysis')}
            </Button>
            <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
              {t('settings.delete_analysis_note')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      {/* Delete Confirmation Dialog - Using reusable component */}
      <CaseDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        showDetailedWarning={true}
        isDeleting={isDeleting}
      />

      {/* Rescan Confirmation Dialog */}
      <Dialog
        open={rescanConfirmOpen}
        onClose={handleRescanCancel}
        aria-labelledby="rescan-dialog-title"
        aria-describedby="rescan-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 10,
          }
        }}
      >
        <DialogTitle id="rescan-dialog-title" sx={{ bgcolor: '#5757b0', color: 'white', display: 'flex', alignItems: 'center' }}>
          <FindInPageIcon sx={{ mr: 1 }} /> {t('settings.rescan_confirm_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="rescan-dialog-description" sx={{ mt: 2 }}>
            <Typography variant="body1" fontWeight="bold" paragraph>
              {t('settings.rescan_confirm_warning')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('settings.rescan_confirm_description')}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleRescanCancel} variant="outlined">
            {t('settings.rescan_confirm_cancel')}
          </Button>
          <Button
            onClick={handleRescanConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#5757b0',
              color: 'white',
              '&:hover': { backgroundColor: '#aaaaca' },
            }}
          >
            {t('settings.rescan_confirm_proceed')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redo Analysis Confirmation Dialog */}
      <Dialog
        open={redoAnalysisConfirmOpen}
        onClose={handleRedoAnalysisCancel}
        aria-labelledby="redo-analysis-dialog-title"
        aria-describedby="redo-analysis-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 10,
          }
        }}
      >
        <DialogTitle id="redo-analysis-dialog-title" sx={{ bgcolor: '#5757b0', color: 'white', display: 'flex', alignItems: 'center' }}>
          <ReplayIcon sx={{ mr: 1 }} /> {t('settings.redo_analysis_confirm_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="redo-analysis-dialog-description" sx={{ mt: 2 }}>
            <Typography variant="body1" fontWeight="bold" paragraph>
              {t('settings.redo_analysis_confirm_warning')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('settings.redo_analysis_confirm_description')}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleRedoAnalysisCancel} variant="outlined">
            {t('settings.redo_analysis_confirm_cancel')}
          </Button>
          <Button
            onClick={handleRedoAnalysisConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#5757b0',
              color: 'white',
              '&:hover': { backgroundColor: '#aaaaca' },
            }}
          >
            {t('settings.redo_analysis_confirm_proceed')}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default Settings;
